var directive_resolver_1 = require('../linker/directive_resolver');
var lang_1 = require('../../facade/lang');
var primitives_1 = require('../../facade/primitives');
var collections_1 = require('../../facade/collections');
var directive_lifecycles_reflector_1 = require('../linker/directive_lifecycles_reflector');
var directive_lifecycle_interfaces_1 = require('../linker/directive_lifecycle_interfaces');
var metadata_directives_1 = require('./metadata_directives');
var metadata_di_1 = require('./metadata_di');
var util_1 = require('./util/util');
/**
 * @internal
 */
var DirectiveProvider = (function () {
    function DirectiveProvider(directiveResolver) {
        this.directiveResolver = directiveResolver;
    }
    /**
     * creates directiveName and DirectiveFactory for angularJS container
     *
     * it produces directive for classes decorated with @Directive with following DDO:
     * ```
     * {
     * require: ['directiveName'],
     * controller: ClassDirective,
     * link: postLinkFn
     * }
     * ```
     *
     * it produces component for classes decorated with @Component with following DDO:
     * ```
     * {
     * require: ['directiveName'],
     * controller: ClassDirective,
     * controllerAs: '$ctrl',
     * template: 'component template string',
     * scope:{},
     * bindToController:{},
     * transclude: false,
     * link: postLinkFn
     * }
     * ```
     * @param type
     * @returns {string|function(): ng.IDirective[]}
     */
    DirectiveProvider.prototype.createFromType = function (type) {
        var metadata = this.directiveResolver.resolve(type);
        var directiveName = lang_1.resolveDirectiveNameFromSelector(metadata.selector);
        var requireMap = this.directiveResolver.getRequiredDirectivesMap(type);
        var lfHooks = directive_lifecycles_reflector_1.resolveImplementedLifeCycleHooks(type);
        var inputs = metadata.inputs, attrs = metadata.attrs, outputs = metadata.outputs, host = metadata.host, queries = metadata.queries, legacy = metadata.legacy;
        var _ddo = {
            controller: _controller,
            link: {
                pre: function () { _ddo._ngOnInitBound(); },
                post: this._createLink(type, metadata, lfHooks)
            },
            // @TODO this will be removed after @Query handling is moved to directiveControllerFactory
            require: this._createRequires(requireMap, directiveName),
            _ngOnInitBound: lang_1.noop
        };
        // Component controllers must be created from a factory. Checkout out
        // util/directive-controller.js for more information about what's going on here
        _controller.$inject = ['$scope', '$element', '$attrs', '$transclude', '$injector'];
        function _controller($scope, $element, $attrs, $transclude, $injector) {
            var locals = { $scope: $scope, $element: $element, $attrs: $attrs, $transclude: $transclude };
            return util_1.directiveControllerFactory(this, type, $injector, locals, requireMap, _ddo);
        }
        // specific DDO augmentation for @Component
        if (metadata instanceof metadata_directives_1.ComponentMetadata) {
            var componentSpecificDDO = {
                scope: {},
                bindToController: this._createComponentBindings(inputs, attrs, outputs),
                controllerAs: DirectiveProvider._controllerAs,
                transclude: DirectiveProvider._transclude
            };
            if (metadata.template && metadata.templateUrl) {
                throw new Error('cannot have both template and templateUrl');
            }
            if (metadata.template) {
                componentSpecificDDO.template = metadata.template;
            }
            if (metadata.templateUrl) {
                componentSpecificDDO.templateUrl = metadata.templateUrl;
            }
            collections_1.StringMapWrapper.assign(_ddo, componentSpecificDDO);
        }
        // allow compile defined as static method on Type
        if (lang_1.isFunction(type.compile)) {
            _ddo.compile = function compile(tElement, tAttrs) {
                var linkFn = type.compile(tElement, tAttrs);
                // if user custom compile fn returns link use that one instead use generated
                return lang_1.isJsObject(linkFn)
                    ? linkFn
                    : this.link;
            };
        }
        // allow link defined as static method on Type override the created one
        // you should not use this very often
        // Note: if you use this any @Host property decorators or lifeCycle hooks wont work
        if (lang_1.isFunction(type.link)) {
            _ddo.link = type.link;
        }
        // legacy property overrides all generated DDO stuff
        var ddo = this._createDDO(_ddo, metadata.legacy);
        return [
            directiveName,
            function directiveFactory() { return ddo; }
        ];
    };
    DirectiveProvider.prototype._createDDO = function (ddo, legacyDDO) {
        return lang_1.assign({}, DirectiveProvider._ddoShell, ddo, legacyDDO);
    };
    /**
     *
     * @param inputs
     * @param attrs
     * @param outputs
     * @returns {{}}
     * @private
     * @internal
     */
    DirectiveProvider.prototype._createComponentBindings = function (inputs, attrs, outputs) {
        if (inputs === void 0) { inputs = []; }
        if (attrs === void 0) { attrs = []; }
        if (outputs === void 0) { outputs = []; }
        var inputsBindings = _extractBindings(inputs, '=');
        var attrsBindings = _extractBindings(attrs, '@');
        var outputsBindings = _extractBindings(outputs, '&');
        return lang_1.assign({}, inputsBindings, attrsBindings, outputsBindings);
    };
    /**
     *
     * @param requireMap
     * @param directiveName
     * @returns {Array}
     * @private
     * @internal
     */
    DirectiveProvider.prototype._createRequires = function (requireMap, directiveName) {
        return [directiveName].concat(collections_1.StringMapWrapper.values(requireMap));
    };
    DirectiveProvider.prototype._processHost = function (host) {
        if (!lang_1.isPresent(host)) {
            return;
        }
        var HOST_BINDING_KEY_REGEX = /^\[.*\]$/;
        var HOST_LISTENER_KEY_REGEX = /^\(.*\)$/;
        var HAS_CLASS_REGEX = /^class./;
        var HAS_ATTR_REGEX = /^attr./;
        var hostStatic = {};
        var hostBindingsRaw = [];
        var hostListeners = {};
        collections_1.StringMapWrapper.forEach(host, function (hostValue, hostKey) {
            var hostMap = (_a = {}, _a[stripBindingOrListenerBrackets(hostKey)] = hostValue, _a);
            if (isStaticHost(hostKey)) {
                lang_1.assign(hostStatic, hostMap);
                return;
            }
            if (isHostBinding(hostKey)) {
                hostBindingsRaw.push(hostMap);
                return;
            }
            if (isHostListener(hostKey)) {
                lang_1.assign(hostListeners, processHostListenerCallback(hostMap));
            }
            var _a;
        });
        var hostBindings = hostBindingsRaw
            .reduce(function (acc, hostBindingObj) {
            var hostObjKey = Object.keys(hostBindingObj)[0];
            var hostObjValue = hostBindingObj[hostObjKey];
            if (HAS_CLASS_REGEX.test(hostObjKey)) {
                acc.classes[hostObjKey.replace(HAS_CLASS_REGEX, '')] = hostObjValue;
                return acc;
            }
            if (HAS_ATTR_REGEX.test(hostObjKey)) {
                acc.attributes[hostObjKey.replace(HAS_ATTR_REGEX, '')] = hostObjValue;
                return acc;
            }
            lang_1.assign(acc.properties, hostBindingObj);
            return acc;
        }, {
            classes: {},
            attributes: {},
            properties: {}
        });
        return {
            hostStatic: hostStatic,
            hostBindings: hostBindings,
            hostListeners: hostListeners
        };
        function isHostBinding(hostKey) {
            return HOST_BINDING_KEY_REGEX.test(hostKey);
        }
        function isHostListener(hostKey) {
            return HOST_LISTENER_KEY_REGEX.test(hostKey);
        }
        function isStaticHost(hostKey) {
            return !(isHostBinding(hostKey) || isHostListener(hostKey));
        }
        function stripBindingOrListenerBrackets(hostKey) {
            return hostKey.replace(/\[|\]|\(|\)/g, '');
        }
        function processHostListenerCallback(hostListener) {
            var eventName = Object.keys(hostListener)[0];
            var cbString = hostListener[eventName];
            var _a = /^(\w+)\(([$\w.,]*)\)$/.exec(cbString), cbMethodName = _a[1], cbMethodArgs = _a[2];
            return (_b = {},
                _b[eventName] = [
                    cbMethodName
                ].concat(cbMethodArgs.split(',').filter(function (argument) { return Boolean(argument); }).map(function (argument) { return argument.trim(); })),
                _b
            );
            var _b;
        }
    };
    /**
     * Directive lifeCycles:
     * - ngOnInit from preLink (all children compiled and DOM ready)
     * - ngAfterContentInit from postLink ( DOM in children ready )
     * - ngOnDestroy from postLink
     *
     * Component lifeCycles:
     * - ngOnInit from preLink (controller require ready)
     * - ngAfterViewInit from postLink ( all children in view+content compiled and DOM ready )
     * - ngAfterContentInit from postLink ( same as ngAfterViewInit )
     * - ngOnDestroy from postLink
     * @param type
     * @param metadata
     * @param lfHooks
     * @private
     * @internal
     */
    DirectiveProvider.prototype._createLink = function (type, metadata, lfHooks) {
        if ((lfHooks.ngAfterContentChecked || lfHooks.ngAfterViewChecked) && collections_1.StringMapWrapper.size(metadata.queries) === 0) {
            throw new Error("\n              Hooks Impl for " + lang_1.stringify(type) + ":\n              ===================================\n              You've implement AfterContentChecked/AfterViewChecked lifecycle, but @ViewChild(ren)/@ContentChild(ren) decorators are not used.\n              we cannot invoke After(Content|View)Checked without provided @Query decorators\n              ");
        }
        // we need to implement this if query are present on class, because during postLink _ngOnChildrenChanged is not yet
        // implemented on controller instance
        if (collections_1.StringMapWrapper.size(metadata.queries)) {
            type.prototype._ngOnChildrenChanged = lang_1.noop;
        }
        var hostProcessed = this._processHost(metadata.host);
        var postLink;
        // postLink
        if (metadata instanceof metadata_directives_1.ComponentMetadata) {
            if ((lfHooks.ngAfterContentInit || lfHooks.ngAfterContentChecked) && !collections_1.StringMapWrapper.getValueFromPath(metadata, 'legacy.transclude')) {
                throw new Error("\n              Hooks Impl for " + lang_1.stringify(type) + ":\n              ===================================\n              You cannot implement AfterContentInit lifecycle, without allowed transclusion.\n              turn transclusion on within decorator like this: @Component({legacy:{transclude:true}})\n              ");
            }
            postLink = function (scope, element, attrs, controller, transclude) {
                var _watchers = [];
                var ctrl = controller[0], requiredCtrls = controller.slice(1);
                _setHostStaticAttributes(element, hostProcessed.hostStatic);
                // setup @HostBindings
                _watchers.push.apply(_watchers, _setHostBindings(scope, element, ctrl, hostProcessed.hostBindings));
                // setup @HostListeners
                _setHostListeners(scope, element, ctrl, hostProcessed.hostListeners);
                var parentCheckedNotifiers = util_1._getParentCheckNotifiers(ctrl, requiredCtrls);
                _watchers.push.apply(_watchers, parentCheckedNotifiers);
                // setup @ContentChild/@ContentChildren/@ViewChild/@ViewChildren
                _setQuery(scope, element, ctrl, metadata.queries);
                // AfterContentInit/AfterViewInit Hooks
                // if there are query defined schedule $evalAsync semaphore
                if (collections_1.StringMapWrapper.size(metadata.queries)) {
                    ctrl._ngOnChildrenChanged(directive_lifecycle_interfaces_1.ChildrenChangeHook.FromView, [
                        parentCheckedNotifiers.forEach(function (cb) { return cb(); }),
                        ctrl.ngAfterViewInit && ctrl.ngAfterViewInit.bind(ctrl),
                        ctrl.ngAfterViewChecked && ctrl.ngAfterViewChecked.bind(ctrl),
                    ]);
                    ctrl._ngOnChildrenChanged(directive_lifecycle_interfaces_1.ChildrenChangeHook.FromContent, [
                        parentCheckedNotifiers.forEach(function (cb) { return cb(); }),
                        ctrl.ngAfterContentInit && ctrl.ngAfterContentInit.bind(ctrl),
                        ctrl.ngAfterContentChecked && ctrl.ngAfterContentChecked.bind(ctrl),
                    ]);
                }
                else {
                    parentCheckedNotifiers.forEach(function (cb) { return cb(); });
                    ctrl.ngAfterViewInit && ctrl.ngAfterViewInit();
                    ctrl.ngAfterContentInit && ctrl.ngAfterContentInit();
                }
                // destroy
                _setupDestroyHandler(scope, element, ctrl, lfHooks.ngOnDestroy, _watchers);
            };
        }
        else {
            // Directive postLink
            if (lfHooks.ngAfterViewInit || lfHooks.ngAfterViewChecked) {
                throw new Error("\n        Hooks Impl for " + lang_1.stringify(type) + ":\n        ===================================\n        You cannot implement AfterViewInit/AfterViewChecked for @Directive,\n        because directive doesn't have View so you probably doing something wrong.\n        @Directive support only AfterContentInit/AfterContentChecked hook which is triggered from postLink\n        ");
            }
            postLink = function (scope, element, attributes, controller, transclude) {
                var _watchers = [];
                var _observers = [];
                var ctrl = controller[0], requiredCtrls = controller.slice(1);
                var _disposables = _createDirectiveBindings(scope, attributes, ctrl, metadata);
                _watchers.push.apply(_watchers, _disposables.watchers);
                _observers.push.apply(_observers, _disposables.observers);
                _setHostStaticAttributes(element, hostProcessed.hostStatic);
                // setup @HostBindings
                _watchers.push.apply(_watchers, _setHostBindings(scope, element, ctrl, hostProcessed.hostBindings));
                // setup @HostListeners
                _setHostListeners(scope, element, ctrl, hostProcessed.hostListeners);
                var parentCheckedNotifiers = util_1._getParentCheckNotifiers(ctrl, requiredCtrls);
                _watchers.push.apply(_watchers, parentCheckedNotifiers);
                // setup @ContentChild/@ContentChildren
                _setQuery(scope, element, ctrl, metadata.queries);
                // AfterContent Hooks
                // if there are query defined schedule $evalAsync semaphore
                if (collections_1.StringMapWrapper.size(metadata.queries)) {
                    ctrl._ngOnChildrenChanged(directive_lifecycle_interfaces_1.ChildrenChangeHook.FromContent, [
                        parentCheckedNotifiers.forEach(function (cb) { return cb(); }),
                        ctrl.ngAfterContentInit && ctrl.ngAfterContentInit.bind(ctrl),
                        ctrl.ngAfterContentChecked && ctrl.ngAfterContentChecked.bind(ctrl)
                    ]);
                }
                else {
                    // no @ContentChild(ren) decorators exist, call just controller init method
                    parentCheckedNotifiers.forEach(function (cb) { return cb(); });
                    ctrl.ngAfterContentInit && ctrl.ngAfterContentInit();
                }
                // destroy
                _setupDestroyHandler(scope, element, ctrl, lfHooks.ngOnDestroy, _watchers, _observers);
            };
        }
        return postLink;
    };
    DirectiveProvider._ddoShell = {
        require: [],
        controller: lang_1.noop,
        link: { pre: lang_1.noop, post: lang_1.noop }
    };
    DirectiveProvider._controllerAs = "$ctrl";
    DirectiveProvider._transclude = false;
    return DirectiveProvider;
})();
exports.DirectiveProvider = DirectiveProvider;
// private helpers
/**
 *
 * @param scope
 * @param element
 * @param ctrl
 * @param implementsNgOnDestroy
 * @param watchersToDispose
 * @param observersToDispose
 * @private
 */
function _setupDestroyHandler(scope, element, ctrl, implementsNgOnDestroy, watchersToDispose, observersToDispose) {
    if (watchersToDispose === void 0) { watchersToDispose = []; }
    if (observersToDispose === void 0) { observersToDispose = []; }
    scope.$on('$destroy', function () {
        if (implementsNgOnDestroy) {
            ctrl.ngOnDestroy();
        }
        watchersToDispose.forEach(function (_watcherDispose) { return _watcherDispose(); });
        observersToDispose.forEach(function (_observerDispose) { return _observerDispose(); });
        element.off();
    });
}
/**
 *
 * @param element
 * @param staticAttributes
 * @private
 */
function _setHostStaticAttributes(element, staticAttributes) {
    element.attr(staticAttributes);
}
/**
 *
 * @param scope
 * @param element
 * @param ctrl
 * @param hostBindings
 * @returns {Array}
 * @internal
 * @private
 */
function _setHostBindings(scope, element, ctrl, hostBindings) {
    // setup @HostBindings
    return _createWatcherByType('classes', hostBindings).concat(_createWatcherByType('attributes', hostBindings), _createWatcherByType('properties', hostBindings));
    /**
     * registers $scope.$watch for appropriate hostBinding
     * the watcher watches property on controller instance
     * @param type
     * @param hostBinding
     * @returns {Array}
     * @private
     */
    function _createWatcherByType(type, hostBinding) {
        var _watchersByType = [];
        collections_1.StringMapWrapper.forEach(hostBinding[type], function (watchPropName, keyToSet) {
            _watchersByType.push(scope.$watch(function () { return ctrl[watchPropName]; }, function (newValue) {
                if (type === 'classes') {
                    element.toggleClass(keyToSet, newValue);
                }
                if (type === 'attributes') {
                    element.attr(keyToSet, newValue);
                }
                if (type === 'properties') {
                    collections_1.StringMapWrapper.setValueInPath(element[0], keyToSet, newValue);
                }
            }));
        });
        return _watchersByType;
    }
}
exports._setHostBindings = _setHostBindings;
/**
 *
 * @param scope
 * @param element
 * @param ctrl
 * @param hostListeners
 * @internal
 * @private
 */
function _setHostListeners(scope, element, ctrl, hostListeners) {
    collections_1.StringMapWrapper.forEach(hostListeners, function (cbArray, eventName) {
        var methodName = cbArray[0], methodParams = cbArray.slice(1);
        element.on(eventName, function (evt) {
            var cbParams = _getHostListenerCbParams(evt, methodParams);
            scope.$applyAsync(function () {
                var noPreventDefault = ctrl[methodName].apply(ctrl, cbParams);
                // HostListener preventDefault if method name returns false,
                // which is default if you don't explicitly return truthy value
                if (!noPreventDefault) {
                    evt.preventDefault();
                }
            });
        });
    });
}
exports._setHostListeners = _setHostListeners;
/**
 * return $event or it's property if found via path
 * @param event
 * @param eventParams
 * @returns {Array}
 * @private
 */
function _getHostListenerCbParams(event, eventParams) {
    var ALLOWED_EVENT_NAME = '$event';
    return eventParams.reduce(function (acc, eventPath) {
        if (!primitives_1.StringWrapper.startsWith(eventPath, ALLOWED_EVENT_NAME)) {
            throw new Error("\n              only $event.* is supported. Please provide correct listener parameter @example: $event,$event.target\n              ");
        }
        if (eventPath === ALLOWED_EVENT_NAME) {
            return acc.concat([event]);
        }
        return acc.concat([collections_1.StringMapWrapper.getValueFromPath(event, eventPath.replace(ALLOWED_EVENT_NAME, ''))]);
    }, []);
}
exports._getHostListenerCbParams = _getHostListenerCbParams;
/**
 *
 * @param scope
 * @param attributes
 * @param ctrl
 * @param metadata
 * @returns {{watchers: Array, observers: Array}}
 * @internal
 * @private
 */
function _createDirectiveBindings(scope, attributes, ctrl, metadata) {
    var _a = metadata.inputs, inputs = _a === void 0 ? [] : _a, _b = metadata.outputs, outputs = _b === void 0 ? [] : _b, _c = metadata.attrs, attrs = _c === void 0 ? [] : _c;
    var _internalWatchers = [];
    var _internalObservers = [];
    // setup @Inputs
    collections_1.StringMapWrapper.forEach(_extractBindings(inputs), function (alias, propName) {
        // set those values as component bindToController does
        ctrl[propName] = scope.$evalAsync(attributes[alias || propName]);
        // register watchers for further changes
        _internalWatchers.push(scope.$watch(attributes[alias || propName], function (newValue) {
            ctrl[propName] = newValue;
        }));
    });
    // setup @Outputs
    collections_1.StringMapWrapper.forEach(_extractBindings(outputs), function (alias, propName) {
        // set those values as component bindToController does
        ctrl[propName] = function () { return scope.$evalAsync(attributes[alias || propName]); };
    });
    // setup @Attrs
    collections_1.StringMapWrapper.forEach(_extractBindings(attrs), function (alias, propName) {
        // set those values as component bindToController does
        ctrl[propName] = attributes[alias || propName];
        // register watchers for further changes
        // The observer function will be invoked once during the next $digest following compilation.
        // The observer is then invoked whenever the interpolated value changes.
        _internalObservers.push(attributes.$observe(alias || propName, function (newValue) {
            ctrl[propName] = newValue;
        }));
    });
    return {
        watchers: _internalWatchers,
        observers: _internalObservers
    };
}
exports._createDirectiveBindings = _createDirectiveBindings;
/**
 *
 * @param bindings
 * @param typeSymbol
 * @param SPLIT_BY
 * @returns {StringMap}
 * @internal
 * @private
 */
function _extractBindings(bindings, typeSymbol, SPLIT_BY) {
    if (typeSymbol === void 0) { typeSymbol = ''; }
    if (SPLIT_BY === void 0) { SPLIT_BY = ':'; }
    return bindings.reduce(function (acc, binding) {
        var _a = binding.split(SPLIT_BY).map(function (part) { return part.trim(); }), name = _a[0], _b = _a[1], alias = _b === void 0 ? '' : _b;
        acc[name] = "" + typeSymbol + alias;
        return acc;
    }, {});
}
exports._extractBindings = _extractBindings;
/**
 * setups watchers for children component/directives provided by @Query decorators
 * @param scope
 * @param element
 * @param ctrl
 * @param queries
 * @private
 */
function _setQuery(scope, element, ctrl, queries) {
    var SEMAPHORE_PROP_NAMES = Object.freeze({
        view: '__readViewChildrenOrderScheduled',
        content: '__readContentChildrenOrderScheduled'
    });
    var DOM_RESOLVER_TYPES = Object.freeze({
        view: 'view',
        content: 'content'
    });
    if (collections_1.StringMapWrapper.size(queries) === 0) {
        return;
    }
    var onChildrenChangedCb = _getOnChildrenResolvers(element, ctrl, queries);
    ctrl.__readContentChildrenOrderScheduled = false;
    ctrl.__readViewChildrenOrderScheduled = false;
    // this is our created _ngOnChildrenChanged which will be called by children directives
    var _ngOnChildrenChanged = function (type, onFirstChangeDoneCb, domResolverCb) {
        if (onFirstChangeDoneCb === void 0) { onFirstChangeDoneCb = []; }
        if (domResolverCb === void 0) { domResolverCb = onChildrenChangedCb; }
        var orderScheduledSemaphorePropName = '';
        var domResolverCbType = '';
        if (type === directive_lifecycle_interfaces_1.ChildrenChangeHook.FromView) {
            orderScheduledSemaphorePropName = SEMAPHORE_PROP_NAMES.view;
            domResolverCbType = DOM_RESOLVER_TYPES.view;
        }
        else if (type === directive_lifecycle_interfaces_1.ChildrenChangeHook.FromContent) {
            domResolverCbType = DOM_RESOLVER_TYPES.content;
            orderScheduledSemaphorePropName = SEMAPHORE_PROP_NAMES.content;
        }
        else {
            throw new Error("_ngOnChildrenChanged: queryType(" + type + ") must be one of FromView|FromContent");
        }
        if (ctrl[orderScheduledSemaphorePropName]) {
            return;
        }
        ctrl[orderScheduledSemaphorePropName] = true;
        // we execute callback within $evalAsync to extend $digest loop count, which will not trigger another
        // $rootScope.$digest === #perfmatters
        scope.$evalAsync(function () {
            // turn semaphore On back again
            ctrl[orderScheduledSemaphorePropName] = false;
            // query DOM and assign instances/jqLite to controller properties
            domResolverCb[domResolverCbType].forEach(function (cb) { return cb(); });
            // when DOM is queried we can execute DirectiveComponent life cycles which have been registered
            // AfterViewInit | AfterContentInit
            onFirstChangeDoneCb.forEach(function (cb) { lang_1.isFunction(cb) && cb(); });
        });
    };
    // this method needs to be called from children which are we querying
    // if they are rendered dynamically/async
    ctrl._ngOnChildrenChanged = _ngOnChildrenChanged.bind(ctrl);
    /**
     * get all callbacks which will be executed withing $scope.$evalAsync,
     * which are querying for DOM elements and gets controller instances from host element children
     * @param element
     * @param ctrl
     * @param queries
     * @returns {view: Function[], content: Function[]}
     * @private
     */
    function _getOnChildrenResolvers(element, ctrl, queries) {
        var _onChildrenChangedCbMap = (_a = {},
            _a[DOM_RESOLVER_TYPES.view] = [],
            _a[DOM_RESOLVER_TYPES.content] = [],
            _a
        );
        collections_1.StringMapWrapper.forEach(queries, function (meta, key) {
            if (meta instanceof metadata_di_1.ViewChildMetadata) {
                _onChildrenChangedCbMap[DOM_RESOLVER_TYPES.view].push(_resolveViewChild(element, ctrl, key, meta));
            }
            if (meta instanceof metadata_di_1.ViewChildrenMetadata) {
                _onChildrenChangedCbMap[DOM_RESOLVER_TYPES.view].push(_resolveViewChildren(element, ctrl, key, meta));
            }
            if (meta instanceof metadata_di_1.ContentChildMetadata) {
                _onChildrenChangedCbMap[DOM_RESOLVER_TYPES.content].push(_resolveContentChild(element, ctrl, key, meta));
            }
            if (meta instanceof metadata_di_1.ContentChildrenMetadata) {
                _onChildrenChangedCbMap[DOM_RESOLVER_TYPES.content].push(_resolveContentChildren(element, ctrl, key, meta));
            }
        });
        return _onChildrenChangedCbMap;
        function _resolveViewChild(element, ctrl, key, meta) {
            return util_1._resolveChildrenFactory(element, ctrl, key, meta.selector, DOM_RESOLVER_TYPES.view, true);
        }
        function _resolveContentChild(element, ctrl, key, meta) {
            return util_1._resolveChildrenFactory(element, ctrl, key, meta.selector, DOM_RESOLVER_TYPES.content, true);
        }
        function _resolveViewChildren(element, ctrl, key, meta) {
            return util_1._resolveChildrenFactory(element, ctrl, key, meta.selector, DOM_RESOLVER_TYPES.view);
        }
        function _resolveContentChildren(element, ctrl, key, meta) {
            return util_1._resolveChildrenFactory(element, ctrl, key, meta.selector, DOM_RESOLVER_TYPES.content);
        }
        var _a;
    }
}
exports._setQuery = _setQuery;
exports.directiveProvider = new DirectiveProvider(new directive_resolver_1.DirectiveResolver());
//# sourceMappingURL=directive_provider.js.map


/*****************
 ** WEBPACK FOOTER
 ** ./~/ng-metadata/src/core/directives/directive_provider.js
 ** module id = 39
 ** module chunks = 0
 **/